import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { FiArrowLeft } from 'react-icons/fi'

export default function TicketDashboard() {
  const { websiteId } = useParams()
  const navigate = useNavigate()
  const [website, setWebsite] = useState(null)
  const [sales, setSales] = useState([])
  const [loading, setLoading] = useState(false)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')

  useEffect(() => {
    if (!websiteId) return

    let intervalId

    const load = async () => {
      await fetchWebsite()
      await fetchSales()
    }

    load()

    // Poll every 20 seconds while on dashboard so new ticket purchases appear quickly
    intervalId = setInterval(() => {
      fetchSales()
    }, 20000)

    return () => {
      if (intervalId) clearInterval(intervalId)
    }
  }, [websiteId, filterStatus, search])

  const fetchWebsite = async () => {
    try {
      const res = await axios.get(`/websites/${websiteId}`)
      setWebsite(res.data.website)
    } catch (err) {
      console.error('[TicketDashboard] fetchWebsite error:', err)
      toast.error('Unable to fetch website details')
      navigate('/user-admin/websites')
    }
  }

  const fetchSales = async () => {
    if (!websiteId) return
    try {
      setLoading(true)
      const params = {
        paymentStatus: filterStatus,
        search,
        page: 1,
        limit: 100
      }
      console.log('[TicketDashboard] Fetching sales for website:', websiteId)
      const res = await axios.get(`/event-tickets/sales/website/${websiteId}`, { params })
      console.log('[TicketDashboard] Full response:', res.data)
      console.log('[TicketDashboard] Sales count:', res.data.sales?.length || 0)
      
      // Handle both new and legacy response formats
      if (res.data.sales && Array.isArray(res.data.sales)) {
        setSales(res.data.sales)
      } else {
        console.warn('[TicketDashboard] Unexpected response format:', res.data)
        setSales([])
      }
    } catch (err) {
      console.error('[TicketDashboard] fetchSales error:', err)
      console.error('[TicketDashboard] Error response:', err.response?.data)
      const errorMessage = err.response?.data?.message || 'Unable to fetch ticket sales'
      
      if (err.response?.status === 403) {
        toast.error('You do not have permission to view this website\'s tickets')
        navigate('/user-admin/websites')
      } else if (err.response?.status === 404) {
        toast.error('Website not found')
        navigate('/user-admin/websites')
      } else {
        toast.error(errorMessage)
      }
      setSales([])
    } finally {
      setLoading(false)
    }
  }

  const handleCheckin = async (saleId) => {
    try {
      await axios.post(`/event-tickets/${saleId}/checkin`, { saleId })
      toast.success('Checked in successfully')
      fetchSales()
    } catch (err) {
      console.error('[TicketDashboard] checkin', err)
      toast.error('Unable to mark check-in')
    }
  }

  const filteredSales = sales.filter(sale => {
    if (!search) return true
    const term = search.toLowerCase()
    return (
      sale.buyerName?.toLowerCase().includes(term) ||
      sale.buyerEmail?.toLowerCase().includes(term) ||
      sale.uniqueTicketId?.toLowerCase().includes(term)
    )
  })

  if (!website) {
    return (
      <div className="p-4">
        <h1 className="text-2xl font-bold mb-4">🎫 Ticket Sales Dashboard</h1>
        <p className="text-gray-600">Loading...</p>
      </div>
    )
  }

  return (
    <div className="p-3 sm:p-4">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
        <button
          onClick={() => navigate('/user-admin/websites')}
          className="flex items-center gap-2 px-3 py-2 sm:py-3 bg-gray-100 hover:bg-gray-200 rounded font-medium transition-colors text-sm sm:text-base"
          title="Back to Website Builder Dashboard"
        >
          <FiArrowLeft className="w-4 h-4" /> <span>Back</span>
        </button>
        <h1 className="text-lg sm:text-2xl font-bold">🎫 {website.name} - Ticket Sales</h1>
      </div>

      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mb-4 flex-wrap">
        <input
          className="input-field flex-1 min-w-[200px] text-sm sm:text-base py-2 sm:py-3"
          placeholder="Search by buyer name/email/ticket ID"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        <select
          className="input-field flex-1 min-w-[150px] text-sm sm:text-base py-2 sm:py-3"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
        >
          <option value="all">All Payments</option>
          <option value="paid">Paid</option>
          <option value="pending">Pending</option>
          <option value="failed">Failed</option>
        </select>

        <button className="btn btn-primary whitespace-nowrap text-sm sm:text-base px-4 py-2 sm:py-3" onClick={fetchSales}>Refresh</button>
      </div>

      {loading ? (
        <div className="py-6 sm:py-8 text-center text-sm sm:text-base">Loading ticket sales...</div>
      ) : filteredSales.length === 0 ? (
        <div className="py-6 sm:py-8 text-center text-xs sm:text-sm text-gray-600">No ticket sales found.</div>
      ) : (
        <div className="overflow-x-auto -mx-3 sm:mx-0">
          <table className="min-w-full text-left text-xs sm:text-sm">
            <thead>
              <tr className="border-b bg-gray-50">
                <th className="px-3 sm:px-4 py-2 sm:py-3 font-medium text-gray-700">Buyer Name</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 font-medium text-gray-700 hidden sm:table-cell">Buyer Email</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 font-medium text-gray-700 hidden md:table-cell">Ticket Type</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 font-medium text-gray-700 hidden lg:table-cell">Qty</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 font-medium text-gray-700">Status</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 font-medium text-gray-700 hidden lg:table-cell">Purchased</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 font-medium text-gray-700 hidden md:table-cell">Ticket ID</th>
                <th className="px-3 sm:px-4 py-2 sm:py-3 font-medium text-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSales.map((sale) => (
                <tr key={sale._id} className="border-b hover:bg-gray-50">
                  <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm font-medium max-w-[100px] truncate">{sale.buyerName}</td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm hidden sm:table-cell max-w-[120px] truncate">{sale.buyerEmail}</td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm hidden md:table-cell">{sale.ticketType}</td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm hidden lg:table-cell">{sale.quantity}</td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      sale.paymentStatus === 'paid' ? 'bg-green-100 text-green-700' :
                      sale.paymentStatus === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {sale.paymentStatus}
                    </span>
                  </td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm hidden lg:table-cell whitespace-nowrap">{new Date(sale.purchaseDate).toLocaleString()}</td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3 text-xs sm:text-sm hidden md:table-cell max-w-[100px] truncate">{sale.uniqueTicketId}</td>
                  <td className="px-3 sm:px-4 py-2 sm:py-3">
                    <button
                      className="btn btn-xs btn-secondary text-xs px-2 py-1"
                      disabled={sale.checkedIn}
                      onClick={() => handleCheckin(sale._id)}
                    >
                      {sale.checkedIn ? 'Checked In' : 'Check In'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
